import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LogoutIcon from '@mui/icons-material/Logout'
import LoyaltyIcon from '@mui/icons-material/Loyalty'
import PaymentIcon from '@mui/icons-material/Payment'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import RedeemIcon from '@mui/icons-material/Redeem'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import { Fragment, useMemo, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { useSessionContext } from '@providers/session'
import { useLocationContext } from '@providers/location'
import { i18WithParams as t } from '@shared/locale'
import type { TRouteNames } from 'app/types'
import { useRoutes } from 'app/use_routes'
import { PetAddOnIcon } from 'components-ui/src/asset/pet_addon_icon'
import { PetMenuIcon } from 'components-ui/src/asset/pet_menu_icon'
import { Hooks, Services, Types } from 'service-api'

interface TMenuItem {
  handle: string
  label: string
  icon: React.ElementType
  routePath: string
  route: TRouteNames
  enabled: boolean
}

const menuItems = [
  {
    handle: 'profile',
    label: t('profileNav.profile'),
    icon: AccountCircleIcon,
    routePath: 'profile/my-profile',
    route: 'myprofile',
    enabled: true,
  },
  {
    handle: 'pets',
    label: t('profileNav.pets'),
    icon: PetAddOnIcon,
    routePath: 'profile/pets',
    route: 'profilePet',
    enabled: true,
  },
  {
    handle: 'cameras',
    label: t('profileNav.cameras'),
    icon: PhotoCameraIcon,
    routePath: 'profile/cameras',
    route: 'profileCameras',
    enabled: true,
  },
  {
    handle: 'bookings',
    label: t('profileNav.bookings'),
    icon: PetMenuIcon,
    routePath: 'profile/bookings',
    route: 'profileBookings',
    enabled: true,
  },
  {
    handle: 'report-cards',
    label: t('profileNav.reportCards'),
    icon: AutoAwesomeMotionOutlinedIcon,
    routePath: 'profile/report-cards',
    route: 'profileReportCards',
    enabled: true,
  },
  {
    handle: 'payment-methods',
    label: t('profileNav.paymentMethods'),
    icon: PaymentIcon,
    routePath: 'profile/payments',
    route: 'profilePayments',
    enabled: true,
  },
  {
    handle: 'memberships',
    label: t('profileNav.memberships'),
    icon: LoyaltyIcon,
    routePath: 'profile/memberships',
    route: 'profileMemberships',
    enabled: true,
  },
  {
    handle: 'packages',
    label: t('profileNav.packages'),
    icon: RedeemIcon,
    routePath: 'profile/packages',
    route: 'profilePackages',
    enabled: true,
  },
  {
    handle: 'agreements',
    label: t('profileNav.agreements'),
    icon: StickyNote2OutlinedIcon,
    routePath: 'profile/agreements',
    route: 'profileAgreements',
    enabled: true,
  },
  {
    handle: 'additional-contacts',
    label: t('profileNav.additionalContacts'),
    icon: PersonAddIcon,
    routePath: 'profile/contacts',
    route: 'profileContacts',
    enabled: true,
  },
  {
    handle: 'logout',
    label: t('profileNav.logout'),
    icon: LogoutIcon,
    routePath: 'profile/logout',
    route: 'logout',
    enabled: true,
  },
] as TMenuItem[]

interface TProfileNavProps {
  onClick?: (menuItem: TMenuItem) => void
}

export const ProfileNav = ({ onClick }: TProfileNavProps) => {
  const { accountName: locationName = '' } = useParams()
  const sessionCtx = useSessionContext()
  const bookingCtx = useBookingContext()
  const { navigateRoute } = useRoutes()
  const { location } = useLocationContext()
  const serviceTypes = Hooks.useClientLocationServiceType(locationName)
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)
  const [hasReportCardsToRender, setHasReportCardsToRender] = useState(false)
  const contracts = Hooks.useClientContracts(locationName, Types.ContractType.ServiceAgreement, {
    enabled: !!sessionCtx.state?.user?.lupId,
  })
  const cameras = Hooks.useClientLocationCamerasAccess(locationName, sessionCtx.state?.user?.lupId || '')

  const hasPackages = useMemo(() => {
    if (serviceTypes.isSuccess) {
      return serviceTypes.data.results.some((serviceType) => serviceType.serviceType?.name === 'packages')
    } else {
      return false
    }
  }, [serviceTypes.data, serviceTypes.isSuccess])

  const hasMemberships = useMemo(() => {
    if (serviceTypes.isSuccess) {
      return serviceTypes.data.results.some((serviceType) => serviceType.serviceType?.name === 'memberships')
    } else {
      return false
    }
  }, [serviceTypes.data, serviceTypes.isSuccess])

  const hasAccessToCameras = useMemo(() => {
    if (cameras?.isSuccess) {
      return cameras?.data?.hasConfig
    } else {
      return false
    }
  }, [cameras?.data, cameras?.isSuccess])

  const hasAccessToReportCards = useMemo(() => {
    if (location?.subscriberGroup) {
      return location?.subscriberGroup.reportCardEnabled
    } else {
      return false
    }
  }, [location?.subscriberGroup])

  useEffect(() => {
    if (hasAccessToReportCards) {
      // if we might be able to show report cards, check if they have any to show
      Services.ClientLocationUserProfileService.get(locationName, sessionCtx.state?.user?.lupId || '', [
        'locationPetProfiles',
      ]).then((data) => {
        // if they don't have pets, then they don't have any report cards, do they?
        if (!data.locationPetProfiles || data.locationPetProfiles.length === 0) {
          setHasReportCardsToRender(false)
        } else {
          // then check that extant pets HAVE report cards
          const preparedPromises = data.locationPetProfiles.map((pet) =>
            Services.ClientReportCardListService.getReportCardListByPetId(locationName, pet.id || '', { limit: 1 })
          )
          Promise.allSettled(preparedPromises).then((res) => {
            const reportCards = res.flatMap((res) => res.value)
            if (reportCards.length > 0) {
              setHasReportCardsToRender(true)
            } else {
              setHasReportCardsToRender(false)
            }
          })
        }
      })
    } else {
      setHasReportCardsToRender(false)
    }
  }, [hasAccessToReportCards])

  const hasContracts = useMemo(
    () => contracts.isSuccess && contracts?.data && contracts?.data?.results?.length > 0,
    [contracts.data, contracts.isSuccess]
  )

  const handleMenuClick = (menuItem: TMenuItem) => {
    if (menuItem.handle === 'logout') {
      setShowLogoutDialog(true)
      return
    }

    onClick?.(menuItem)
    setTimeout(() => navigateRoute(menuItem.route, { accountName: locationName }), 0)
  }

  const handleLogout = () => {
    setShowLogoutDialog(false)
    bookingCtx.dispatch({ type: 'RESET_STATE_FULL' })
    sessionCtx.dispatch({ type: 'SIGNOUT' })
    return
  }

  return (
    <Fragment>
      {menuItems
        .filter(
          (item) =>
            item.enabled &&
            !(
              (item.handle === 'packages' && !hasPackages) ||
              (item.handle === 'agreements' && !hasContracts) ||
              (item.handle === 'memberships' && !hasMemberships) ||
              (item.handle === 'cameras' && !hasAccessToCameras) ||
              (item.handle === 'report-cards' && (!hasAccessToReportCards || !hasReportCardsToRender))
            )
        )
        .map((item) => (
          <MenuItem
            key={item.handle}
            id={item.handle}
            selected={window.location.pathname.includes(item.routePath)}
            onClick={() => handleMenuClick(item)}
          >
            <ListItemIcon>
              <item.icon />
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      <Dialog open={showLogoutDialog} onClose={() => setShowLogoutDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('profile.navBlocker.title')}</DialogTitle>
        <DialogContent>{t('profile.logOut.dialogContent')}</DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', padding: '8px 16px 16px 16px' }}>
          <Button onClick={handleLogout} color="secondary" variant="contained">
            {t('loginMenu.logOut')}
          </Button>
          <Button onClick={() => setShowLogoutDialog(false)} color="primary">
            {t('profile.navBlocker.back')}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  )
}

export const ProfileNavMenu = () => {
  return (
    <MenuList>
      <ProfileNav />
    </MenuList>
  )
}
